import { useEffect, useCallback, useImperativeHandle, useMemo } from "react";
import { useAppDispatch } from "@lib/stores";
import { db } from "@modules/local/db";
import { EmptyParameters, DataResult, ContentComponentRefType } from "../types";
import { useContentEntityInfoLiveQuery } from "./useContentEntityInfoLiveQuery";
import { useContentEntityLiveQuery } from "./useContentEntityLiveQuery";
import * as Api from "@modules/api";
import {
  getUniqueId,
  getContent,
  processParameterValue,
  getMoreContent,
  getInfoFromUniqueId,
  defaultPageSize,
} from "../helpers/content";
import { useAppLiveQuery } from "@modules/local/hooks";
import { ComponentsActions } from "@lib/fetchers/components";
import { useLoader } from "@utils/hooks/useLoader";

export type ComponentDataResult<
  TParameters = EmptyParameters,
  TItemParameters = EmptyParameters,
> = DataResult<TParameters> & {
  componentData: Api.Server.ComponentWithUniqueIdOutput | undefined;
  getItemsParameterValues: <TKey extends keyof TItemParameters>(
    keys: TKey[]
  ) => {
    content: Api.Server.ContentEntityOutput;
    values: {
      [K in TKey]?: TItemParameters[K];
    };
  }[];
};

export type OnItemPress<
  T extends {
    [key: string]: string | string[] | number | boolean | null | undefined;
  },
> = (
  content: Api.Server.ContentEntityOutput,
  item: T,
  extra: { isLocked: boolean | undefined; narratorId?: number }
) => void | Promise<void>;

export type UseComponentDataArgs = {
  id?: string;
  // componentUniqueId?: string;
  component?: Api.Server.ComponentWithUniqueIdOutput;
  context?: Api.Server.ContentEntityOutput | undefined | null;
  pageNumber?: number;
  pageSize?: number;
  onPress?: (content: Api.Server.ContentEntityOutput) => void | Promise<void>;
  onItemPress?: OnItemPress<{}>;
  criteria?: string;
  className?: string;
};

export type UseComponentDataArgsOutput = {
  onItemPress: OnItemPress<{}>;
  id?: string;
  // componentUniqueId?: string;
  component?: Api.Server.ComponentWithUniqueIdOutput;
  context?: Api.Server.ContentEntityOutput | undefined | null;
  pageNumber?: number;
  pageSize?: number;
  onPress?: (content: Api.Server.ContentEntityOutput) => void | Promise<void>;
  criteria?: string;
  className?: string;
};

export function useComponentData<
  TParameters = EmptyParameters,
  TItemParameters = EmptyParameters,
>(
  {
    id,
    component,
    // componentUniqueId,
    context,
    pageNumber,
    pageSize,
    criteria,
  }: UseComponentDataArgsOutput,
  ref?: React.ForwardedRef<ContentComponentRefType>
): ComponentDataResult<TParameters, TItemParameters> {
  if (!id && !component)
    throw new Error("id or componentUniqueId or component must be provided");

  // if (id) throw new Error("id with componentUniqueId must be provided");

  const dispatch = useAppDispatch();

  const { isLoading, startFunction } = useLoader();

  const uniqueId = getUniqueId(
    "component",
    id ?? component?.id,
    context?.id,
    context?.contentEntityTypeId,
    criteria
  );

  const { result: componentData, isLoading: isComponentDBLoading } =
    useAppLiveQuery(() => component ?? db.components.get(id!), [id]);

  const finalPageSize = componentData?.pageSize ?? pageSize ?? defaultPageSize;

  const { result: contentsResult, isLoading: isContentEntityDBLoading } =
    useContentEntityLiveQuery(uniqueId, finalPageSize);

  const { result: contentInfo, isLoading: isContentEntityInfoDBLoading } =
    useContentEntityInfoLiveQuery(uniqueId);

  const contents =
    contentsResult?.id === uniqueId ? contentsResult.data : undefined;

  // console.log(contentsResult, uniqueId, contents);

  const getComponentAsync = async () => {
    if (!id) return;

    await ComponentsActions.getComponentByIdAsync(id);
  };

  useEffect(() => {
    if (componentData && componentData.id === id) return;

    startFunction(async () => {
      await getComponentAsync();
    });
  }, [id]);

  useEffect(() => {
    if ((componentData && componentData.id === id) || component) {
      startFunction(async () => {
        await getContent({
          context,
          dispatch,
          pageNumber: componentData?.pageNumber ?? pageNumber,
          pageSize: finalPageSize,
          uniqueId,
          selectorsData: componentData,
          criteria,
        });
      });
    }
  }, [
    JSON.stringify(componentData?.selectors),
    JSON.stringify(componentData?.sorters),
    componentData?.pageNumber ?? pageNumber,
    finalPageSize,
    context?.id,
    context?.contentItemType,
    criteria,
    component?.id,
  ]);

  const content = contents?.[0];

  // const content = useMemo(() => {
  //   if (!componentData) return undefined;

  //   if (isContentEntitySelector(componentData, context)) {
  //     return context;
  //   }

  //   const firstContent = contents && !contents.length ? null : contents?.[0];

  //   if (componentData) {
  //     return firstContent;
  //   }

  //   // const content = !componentData
  //   //   ? firstContent
  //   //   : !firstContent && context
  //   //     ? context
  //   //     : firstContent;

  //   return undefined;
  // }, [contents, componentData, context]);

  const getParameterValues = useCallback(
    <TKey extends keyof TParameters>(
      keys: TKey[]
    ): {
      [K in TKey]?: TParameters[K];
    } => {
      if (componentData === undefined) {
        return {};
      }
      return keys.reduce(
        (acc, key) => ({
          ...acc,
          [key]: processParameterValue<TParameters, TKey>(
            key,
            componentData,
            content || undefined
          ),
        }),
        {} as { [K in TKey]: TParameters[K] }
      );
    },
    [
      componentData,
      content,
      // content?.parameters?.map(p => p.value)
    ]
  );

  const getItemsParameterValues = useCallback(
    <TKey extends keyof TItemParameters>(
      keys: TKey[]
    ): {
      content: Api.Server.ContentEntityOutput;
      values: { [K in TKey]?: TItemParameters[K] };
    }[] => {
      const itemTemplate = componentData?.itemTemplate;
      if (itemTemplate === undefined) {
        return (
          contents?.map((c) => ({
            content: c,
            values: {},
          })) ?? []
        );
      }

      return (
        contents?.map((c) => ({
          content: c,
          values: keys.reduce(
            (acc, key) => ({
              ...acc,
              [key]: processParameterValue<TItemParameters, TKey>(
                key,
                itemTemplate,
                c
              ),
            }),
            {} as { [K in TKey]: TItemParameters[K] }
          ),
        })) ?? []
      );
    },
    [
      componentData?.itemTemplate,
      contents,
      // content?.parameters?.map(p => p.value)
    ]
  );

  const refresh = useCallback(async () => {
    await startFunction(async () => {
      await getComponentAsync();
      await getContent({
        context,
        dispatch,
        selectorsData: componentData,
        uniqueId,
        pageNumber,
        pageSize: finalPageSize,
        criteria,
      });
    });
  }, [
    JSON.stringify(componentData?.selectors),
    JSON.stringify(componentData?.sorters),
    context?.id,
    context?.contentItemType,
    pageNumber,
    finalPageSize,
    criteria,
  ]);

  const getMore = useCallback(async () => {
    await startFunction(async () => {
      await getMoreContent({
        context,
        dispatch,
        selectorsData: componentData,
        uniqueId,
        criteria,
      });
    });
  }, [
    JSON.stringify(componentData?.selectors),
    JSON.stringify(componentData?.sorters),
    context?.id,
    context?.contentItemType,
    criteria,
  ]);

  const result: ComponentDataResult<TParameters, TItemParameters> = {
    componentData: componentData,
    contents: contents,
    meta: contentInfo ?? {},
    content: content,
    // TODO: Check these
    isLoading: isLoading,
    isLoadingMore: contentInfo?.uniqueId === "loading",
    getParameterValues,
    getItemsParameterValues,
    refresh,
    uniqueId,
    getMore,
  };

  useImperativeHandle(ref, () => result);
  return result;
}

export function useComponentDataByUniqueId<TParameters = EmptyParameters>(
  props: Omit<Omit<UseComponentDataArgsOutput, "context">, "component"> & {
    componentUniqueId: string;
  },
  ref?: React.ForwardedRef<ContentComponentRefType>
): ComponentDataResult<TParameters> {
  const { contentEntityTypeId, contentId, criteria, uiId } =
    getInfoFromUniqueId(props.componentUniqueId);

  const { result: context, isLoading } = useAppLiveQuery(
    () => db.entities.get(contentId ?? ""),
    [contentId]
  );

  // const context2 = useAppSelector((s) =>
  //   contentId == undefined
  //     ? undefined
  //     : s.content.data.find((c) => c.id === contentId)
  // );

  return useComponentData<TParameters>(
    {
      ...props,
      context: context ?? {
        id: contentId,
        contentEntityTypeId,
      },
      criteria,
      id: uiId,
    },
    ref
  );
}
